// Per-conversation draft of the user's typed-but-unsent message in the query
// box. Survives logout (manual or otherwise) so a long question doesn't have
// to be retyped — the Restore banner on next login re-offers the draft.
//
// Per-user isolation
// -----------------
// Drafts are namespaced by the currently-signed-in user_id, so on a shared
// clinical workstation user B never sees user A's typed PHI. The user_id is
// saved to localStorage by services/api.js via adoptUser() — from the login
// response (authAPI.login / authAPI.loginWithGoogle) and, crucially, from
// every /api/auth/me answer, which is the only JS the TELUS PingID flow runs.
// It is read here on each draft operation.
//
// When NO user is signed in (pre-login, or after the user_id has been
// cleared) draft operations are no-ops — there is no "anonymous" namespace
// because anonymous drafts cannot be safely attributed back to a user on
// the next login.
//
// Storage layout (keys are scoped by user_id suffix):
//   prism_drafts:<user_id>         → { [conversationId | 'new-chat']: text }
//   prism_drafts_last_id:<user_id> → string — most-recently-edited id
//   prism_current_user_id          → string — the live signed-in user id
//
// Shared between Chat.js (read/write/clear) and IdleTimeout.js (clear-only,
// for the user-initiated "Log out now" path — kept as an emergency wipe
// even though manual logout now preserves drafts by default).

const CURRENT_USER_KEY = 'prism_current_user_id';
const DRAFTS_KEY_PREFIX  = 'prism_drafts:';
const LAST_ID_KEY_PREFIX = 'prism_drafts_last_id:';

const getCurrentUserId = () => {
  try { return localStorage.getItem(CURRENT_USER_KEY) || null; } catch { return null; }
};

export const setCurrentUserId = (userId) => {
  try {
    if (userId) localStorage.setItem(CURRENT_USER_KEY, String(userId));
    else localStorage.removeItem(CURRENT_USER_KEY);
  } catch {}
};

const draftsKey = (userId) => `${DRAFTS_KEY_PREFIX}${userId}`;
const lastIdKey = (userId) => `${LAST_ID_KEY_PREFIX}${userId}`;

export const readDrafts = () => {
  const userId = getCurrentUserId();
  if (!userId) return {};
  try {
    const raw = localStorage.getItem(draftsKey(userId));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const writeDrafts = (obj) => {
  const userId = getCurrentUserId();
  if (!userId) return;
  try {
    if (obj && Object.keys(obj).length > 0) {
      localStorage.setItem(draftsKey(userId), JSON.stringify(obj));
    } else {
      localStorage.removeItem(draftsKey(userId));
    }
  } catch {}
};

export const getDraft = (id) => {
  if (!id) return '';
  const drafts = readDrafts();
  return typeof drafts[id] === 'string' ? drafts[id] : '';
};

export const setDraft = (id, text) => {
  if (!id) return;
  const userId = getCurrentUserId();
  if (!userId) return;  // No-op until login is complete.
  const drafts = readDrafts();
  if (text && text.length > 0) {
    drafts[id] = text;
    try { localStorage.setItem(lastIdKey(userId), id); } catch {}
  } else {
    delete drafts[id];
    // If we just cleared the marker's target, demote the marker so the next
    // return doesn't route the user to an empty draft.
    try {
      if (localStorage.getItem(lastIdKey(userId)) === id) {
        localStorage.removeItem(lastIdKey(userId));
      }
    } catch {}
  }
  writeDrafts(drafts);
};

// Returns the id of the most recently edited draft (a conversation id or
// 'new-chat'), or null when there is no live draft for the current user.
export const getLastDraftId = () => {
  const userId = getCurrentUserId();
  if (!userId) return null;
  try {
    const id = localStorage.getItem(lastIdKey(userId));
    if (!id) return null;
    const drafts = readDrafts();
    return (typeof drafts[id] === 'string' && drafts[id].length > 0) ? id : null;
  } catch { return null; }
};

// Wipe ONLY the current user's drafts. Other users' draft namespaces are
// untouched so multi-user workstations stay clean.
export const clearAllDrafts = () => {
  const userId = getCurrentUserId();
  if (!userId) return;
  try {
    localStorage.removeItem(draftsKey(userId));
    localStorage.removeItem(lastIdKey(userId));
  } catch {}
};

// One-time migration of pre-namespacing drafts. The previous version of
// this module stored drafts under bare `prism_drafts` / `prism_drafts_last_id`
// keys without a user suffix. On first load by a logged-in user, fold any
// leftover unnamespaced drafts into their per-user namespace and remove the
// old keys. This runs at most once: the legacy keys are deleted after copy.
//
// We CAN'T tell which historical user typed the leftover drafts, so the
// fold is best-effort — if a different user lands first after deploy they
// inherit the legacy drafts. Acceptable on dev; remove this helper after
// one deploy cycle.
export const migrateLegacyDraftsForUser = (userId) => {
  if (!userId) return;
  try {
    const rawLegacy = localStorage.getItem('prism_drafts');
    const legacyLast = localStorage.getItem('prism_drafts_last_id');
    if (!rawLegacy && !legacyLast) return;
    if (rawLegacy) {
      const userKey = draftsKey(userId);
      if (!localStorage.getItem(userKey)) {
        localStorage.setItem(userKey, rawLegacy);
      }
      localStorage.removeItem('prism_drafts');
    }
    if (legacyLast) {
      const userLastKey = lastIdKey(userId);
      if (!localStorage.getItem(userLastKey)) {
        localStorage.setItem(userLastKey, legacyLast);
      }
      localStorage.removeItem('prism_drafts_last_id');
    }
  } catch {}
};

// Establish (or re-establish) the draft namespace for `userId`. Idempotent and
// safe to call on every confirmed-session response.
//
// This exists because setting the namespace only in the explicit JS login
// handlers was not enough. TELUS PingID sign-in is a server-side OAuth2
// redirect: the browser leaves for /api/auth/telus/login and comes back on a
// 302 with the session cookie already set, so authAPI.login /
// authAPI.loginWithGoogle never run. prism_current_user_id therefore stayed
// empty for SSO sessions, and because every read/write here no-ops without a
// user id, the whole draft feature silently did nothing for them. /api/auth/me
// is the one call that path does make, so that is where the namespace gets
// adopted.
export const adoptUser = (userId) => {
  if (!userId) return;
  const id = String(userId);
  if (getCurrentUserId() === id) return;  // Already ours — nothing to migrate.
  migrateLegacyDraftsForUser(id);
  setCurrentUserId(id);
};
